import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { userAddressService } from '../../../services/userAddressService';
import { userPaymentMethodService } from '../../../services/userPaymentMethodService';
import { supabase } from '../../../lib/supabase';
import { CreditCard, MapPin, Mail, Check, Plus, Trash2 } from 'lucide-react';

const loadStripeSdk = () => {
  return new Promise((resolve) => {
    if (window.Stripe) {
      resolve(window.Stripe((import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '').trim()));
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/';
    script.onload = () => {
      resolve(window.Stripe((import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '').trim()));
    };
    document.head.appendChild(script);
  });
};

export default function AddressAndPaymentManager() {
  const [addresses, setAddresses] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [editingPaymentMethod, setEditingPaymentMethod] = useState(null);
  
  // Stripe Refs & State
  const stripeRef = React.useRef(null);
  const elementsRef = React.useRef(null);
  const [stripeReady, setStripeReady] = useState(false);
  const [detectedBrand, setDetectedBrand] = useState('Card');

  const [addressFormData, setAddressFormData] = useState({
    addressType: 'mailing',
    firstName: '',
    lastName: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'US',
    phone: '',
    isDefault: false
  });

  const [paymentFormData, setPaymentFormData] = useState({
    type: 'credit_card',
    nickname: '',
    cardHolderName: '',
    cardNumber: '',
    cvv: '',
    expiryDate: '',
    isDefault: false
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    let mounted = true;
    if (showPaymentForm && !editingPaymentMethod) {
      loadStripeSdk().then(stripe => {
        if (!mounted) return;
        stripeRef.current = stripe;
        const elements = stripe.elements();
        elementsRef.current = elements;

        const style = {
            base: {
                color: '#1E293B', // slate-800 matches app foreground
                fontFamily: '"Inter", sans-serif',
                fontSmoothing: 'antialiased',
                fontSize: '14px',
                '::placeholder': {
                    color: '#64748B' // slate-500 matches muted-foreground
                }
            },
            invalid: {
                color: '#EF4444', // red-500
                iconColor: '#EF4444'
            }
        };

        const cardNumber = elements.create('cardNumber', { style, showIcon: false });
        cardNumber.mount('#card-number-element');
        cardNumber.on('change', (e) => {
            if (e.brand) setDetectedBrand(e.brand.charAt(0).toUpperCase() + e.brand.slice(1));
        });

        const cardExpiry = elements.create('cardExpiry', { style });
        cardExpiry.mount('#card-expiry-element');

        const cardCvc = elements.create('cardCvc', { style });
        cardCvc.mount('#card-cvc-element');
        
        setStripeReady(true);
      });
    }
    return () => { mounted = false; };
  }, [showPaymentForm, editingPaymentMethod]);

  const loadData = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const [addrData, payData] = await Promise.all([
        userAddressService.getUserAddresses(user.id),
        userPaymentMethodService.getUserPaymentMethods(user.id)
      ]);

      setAddresses((addrData || []).filter(addr => addr.addressType !== 'temporary'));
      setPaymentMethods((payData || []).filter(m => !m.isBusiness));
    } catch (err) {
      console.error('Error loading address/payment data:', err);
      setError('Failed to load your information');
    } finally {
      setLoading(false);
    }
  };

  const handleAddressSubmit = async (e) => {
    e.preventDefault();
    try {
      setError('');
      const { data: { user } } = await supabase.auth.getUser();
      
      if (editingAddress) {
        await userAddressService.updateAddress(editingAddress.id, addressFormData);
      } else {
        await userAddressService.createAddress(user.id, addressFormData);
      }
      
      await loadData();
      setShowAddressForm(false);
      setEditingAddress(null);
      resetAddressForm();
    } catch (err) {
      setError(err.message || 'Failed to save address');
    }
  };

  const detectCardBrand = (number) => {
    const cleanNumber = number.replace(/\s/g, '');
    if (!cleanNumber) return '';
    const firstDigit = cleanNumber.charAt(0);
    if (firstDigit === '4') return 'Visa';
    if (firstDigit === '5') return 'MasterCard';
    if (firstDigit === '3') return 'Amex';
    if (firstDigit === '6') return 'Discover';
    return 'Card';
  };

  const formatCardNumber = (value) => {
    const clean = value.replace(/\D/g, '');
    
    // Amex (4-6-5), Max 15
    if (clean.startsWith('34') || clean.startsWith('37')) {
      const limited = clean.slice(0, 15);
      if (limited.length <= 4) return limited;
      if (limited.length <= 10) return `${limited.slice(0, 4)} ${limited.slice(4)}`;
      return `${limited.slice(0, 4)} ${limited.slice(4, 10)} ${limited.slice(10, 15)}`;
    }

    // Default (Groups of 4), Max 16
    const limited = clean.slice(0, 16);
    const parts = [];
    for (let i = 0; i < limited.length; i += 4) {
      parts.push(limited.slice(i, i + 4));
    }
    return parts.join(' ');
  };

  const formatExpiryDate = (value) => {
    const clean = value.replace(/\D/g, '');
    if (clean.length >= 3) {
      return `${clean.slice(0, 2)}/${clean.slice(2, 4)}`;
    }
    return clean;
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    try {
      setError('');
      const { data: { user } } = await supabase.auth.getUser();

      if (editingPaymentMethod) {
        await userPaymentMethodService.updatePaymentMethod(editingPaymentMethod.id, {
          nickname: paymentFormData.nickname,
          isDefault: paymentFormData.isDefault
        });
      } else {
        // 1. Validate Holder Name
        if (!paymentFormData.cardHolderName) {
          throw new Error('Please enter Cardholder Name');
        }

        // 2. Get Stripe & Elements
        const stripe = stripeRef.current;
        const elements = elementsRef.current;
        if (!stripe || !elements) throw new Error('Payment system not initialized. Please try refreshing.');

        const cardElement = elements.getElement('cardNumber');

        // 3. Tokenize (Client Side)
        const { error, paymentMethod } = await stripe.createPaymentMethod({
            type: 'card',
            card: cardElement,
            billing_details: {
                name: paymentFormData.cardHolderName,
                email: user.email
            },
        });

        if (error) {
            throw new Error(error.message);
        }

        // 4. Ensure Customer Exists (Server Side)
        let customerRes;
        try {
            customerRes = await fetch('/payment-gateway/create-customer', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify({
                    email: user.email,
                    name: paymentFormData.cardHolderName,
                    metadata: { userId: user.id }
                })
            });
        } catch (networkErr) {
            throw new Error('Network error (create-customer): ' + networkErr.message);
        }
        
        if (!customerRes.ok) throw new Error('Failed to register customer with payment provider');
        const { customerId } = await customerRes.json();

        // 5. Attach Payment Method (Server Side)
        let attachRes;
        try {
            attachRes = await fetch('/payment-gateway/attach-payment-method', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify({
                    paymentMethodId: paymentMethod.id,
                    customerId: customerId
                })
            });
        } catch (networkErr) {
            throw new Error('Network error (attach-payment): ' + networkErr.message);
        }
        
        if (!attachRes.ok) throw new Error('Failed to attach payment method');

        // 6. Save to Database
        const brand = paymentMethod.card.brand;
        const capitalizedBrand = brand ? brand.charAt(0).toUpperCase() + brand.slice(1) : 'Card';

        await userPaymentMethodService.createPaymentMethod(user.id, {
          ...paymentFormData,
          // We pass the last4 directly from Stripe response
          cardLastFour: paymentMethod.card.last4,
          cardBrand: capitalizedBrand,
          expiryMonth: paymentMethod.card.exp_month,
          expiryYear: paymentMethod.card.exp_year,
          stripePaymentMethodId: paymentMethod.id,
          stripeCustomerId: customerId
        });
      }
      
      await loadData();
      setShowPaymentForm(false);
      resetPaymentForm();
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to save payment method. Please check your entries.');
    }
  };

  const handleSetDefaultPayment = async (id) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      await userPaymentMethodService.setDefaultPaymentMethod(user.id, id);
      await loadData();
    } catch (err) {
      setError('Failed to set default payment method');
    }
  };

  const handleDeleteAddress = async (id) => {
    if (!window.confirm('Are you sure you want to delete this address?')) return;
    try {
      await userAddressService.deleteAddress(id);
      await loadData();
    } catch (err) {
      setError('Failed to delete address');
    }
  };

  const handleDeletePayment = async (method) => {
    if (method.isDefault) {
      setError('The default payment method cannot be deleted. Please set another method as default first.');
      return;
    }
    if (paymentMethods.length <= 1) {
      setError('At least one payment method is required.');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this payment method?')) return;
    try {
      await userPaymentMethodService.deletePaymentMethod(method.id);
      await loadData();
    } catch (err) {
      setError('Failed to delete payment method');
    }
  };

  const resetAddressForm = () => {
    setAddressFormData({
      addressType: 'mailing',
      firstName: '',
      lastName: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'US',
      phone: '',
      isDefault: false
    });
  };

  const resetPaymentForm = () => {
    setPaymentFormData({
      type: 'credit_card',
      nickname: '',
      cardHolderName: '',
      cardNumber: '',
      cvv: '',
      expiryDate: '',
      isDefault: false
    });
    setEditingPaymentMethod(null);
  };

  const handleEditPayment = (method) => {
    setEditingPaymentMethod(method);
    setPaymentFormData({
      type: 'credit_card',
      nickname: method.nickname || '',
      cardHolderName: method.cardHolderName || '',
      cardNumber: method.cardLastFour ? `**** **** **** ${method.cardLastFour}` : '',
      cvv: '***',
      expiryDate: method.cardExpMonth && method.cardExpYear 
        ? `${String(method.cardExpMonth).padStart(2, '0')}/${String(method.cardExpYear).slice(-2)}` 
        : '',
      isDefault: method.isDefault
    });
    setShowPaymentForm(true);
  };

  const handleEditAddress = (address) => {
    setEditingAddress(address);
    setAddressFormData({
      addressType: address.addressType,
      firstName: address.firstName,
      lastName: address.lastName,
      addressLine1: address.addressLine1,
      addressLine2: address.addressLine2 || '',
      city: address.city,
      state: address.state,
      postalCode: address.postalCode,
      country: address.country,
      phone: address.phone || '',
      isDefault: address.isDefault
    });
    setShowAddressForm(true);
  };

  if (loading) return <div className="p-4 text-center text-muted-foreground">Loading...</div>;

  return (
    <div className="space-y-8">
      {/* Addresses Section */}
      <section className="space-y-4">
        <div className="flex items-center justify-between border-b border-border pb-2">
          <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
            <MapPin className="w-5 h-5 text-primary" />
            Addresses
          </h3>
          {!showAddressForm && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => { resetAddressForm(); setShowAddressForm(true); }}
              className="flex items-center gap-1"
            >
              <Plus className="w-4 h-4" /> Add New
            </Button>
          )}
        </div>

        {error && (
          <div className="text-sm text-error bg-error/10 p-3 rounded-lg flex items-start gap-2 animate-in fade-in slide-in-from-top-1">
            <Icon name="AlertCircle" size={16} className="mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {showAddressForm ? (
          <form onSubmit={handleAddressSubmit} className="bg-muted/30 p-4 rounded-lg border border-border space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Address Type</label>
                <select 
                  className="w-full h-10 px-3 rounded-lg border border-border bg-background text-sm focus:ring-2 focus:ring-primary/20 outline-none"
                  value={addressFormData.addressType}
                  onChange={(e) => setAddressFormData({...addressFormData, addressType: e.target.value})}
                >
                  <option value="mailing">Mailing</option>
                  <option value="shipping">Shipping</option>
                  <option value="billing">Billing</option>
                  <option value="both">Both (Shipping & Billing)</option>
                </select>
              </div>
              <div className="flex items-end h-10">
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <input 
                    type="checkbox" 
                    className="w-4 h-4 rounded border-border text-primary focus:ring-primary/20"
                    checked={addressFormData.isDefault}
                    onChange={(e) => setAddressFormData({...addressFormData, isDefault: e.target.checked})}
                  />
                  Set as default
                </label>
              </div>
              <Input 
                label="First Name" 
                required 
                value={addressFormData.firstName}
                onChange={(e) => setAddressFormData({...addressFormData, firstName: e.target.value})}
              />
              <Input 
                label="Last Name" 
                required 
                value={addressFormData.lastName}
                onChange={(e) => setAddressFormData({...addressFormData, lastName: e.target.value})}
              />
              <div className="md:col-span-2">
                <Input 
                  label="Address Line 1" 
                  required 
                  value={addressFormData.addressLine1}
                  onChange={(e) => setAddressFormData({...addressFormData, addressLine1: e.target.value})}
                />
              </div>
              <div className="md:col-span-2">
                <Input 
                  label="Address Line 2 (Optional)" 
                  value={addressFormData.addressLine2}
                  onChange={(e) => setAddressFormData({...addressFormData, addressLine2: e.target.value})}
                />
              </div>
              <Input 
                label="City" 
                required 
                value={addressFormData.city}
                onChange={(e) => setAddressFormData({...addressFormData, city: e.target.value})}
              />
              <Input 
                label="State" 
                required 
                value={addressFormData.state}
                onChange={(e) => setAddressFormData({...addressFormData, state: e.target.value})}
              />
              <Input 
                label="Postal Code" 
                required 
                value={addressFormData.postalCode}
                onChange={(e) => setAddressFormData({...addressFormData, postalCode: e.target.value})}
              />
              <Input 
                label="Country" 
                required 
                value={addressFormData.country}
                onChange={(e) => setAddressFormData({...addressFormData, country: e.target.value})}
              />
              <Input 
                label="Phone" 
                value={addressFormData.phone}
                onChange={(e) => setAddressFormData({...addressFormData, phone: e.target.value})}
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="ghost" type="button" onClick={() => { setShowAddressForm(false); setEditingAddress(null); }}>Cancel</Button>
              <Button variant="primary" type="submit">{editingAddress ? 'Update' : 'Save'} Address</Button>
            </div>
          </form>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {addresses.length > 0 ? (
              addresses.map((addr) => (
                <div key={addr.id} className="p-4 border border-border rounded-lg bg-card hover:bg-muted/20 transition-smooth relative group">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-bold uppercase tracking-wider text-primary bg-primary/10 px-2 py-0.5 rounded">
                      {addr.addressType === 'both' ? 'Shipping & Billing' : addr.addressType}
                    </span>
                    {addr.isDefault && (
                      <span className="text-[10px] font-medium text-accent bg-accent/10 px-2 py-0.5 rounded flex items-center gap-1">
                        <Check className="w-3 h-3" /> Default
                      </span>
                    )}
                  </div>
                  <p className="font-medium text-foreground">{addr.firstName} {addr.lastName}</p>
                  <p className="text-sm text-muted-foreground">{addr.addressLine1}</p>
                  {addr.addressLine2 && <p className="text-sm text-muted-foreground">{addr.addressLine2}</p>}
                  <p className="text-sm text-muted-foreground">{addr.city}, {addr.state} {addr.postalCode}</p>
                  <p className="text-sm text-muted-foreground">{addr.country}</p>
                  
                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => handleEditAddress(addr)} className="p-1.5 hover:bg-muted rounded text-muted-foreground hover:text-foreground">
                      <Icon name="Edit" size={14} />
                    </button>
                    {!addr.isDefault && (
                      <button onClick={() => handleDeleteAddress(addr.id)} className="p-1.5 hover:bg-error/10 rounded text-muted-foreground hover:text-error">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-2 text-center py-6 text-muted-foreground bg-muted/20 rounded-lg border border-dashed border-border">
                No addresses saved yet.
              </div>
            )}
          </div>
        )}
      </section>

      {/* Payment Methods Section */}
      <section className="space-y-4">
        <div className="flex items-center justify-between border-b border-border pb-2">
          <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
            <CreditCard className="w-5 h-5 text-primary" />
            Payment Methods
          </h3>
          {!showPaymentForm && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => { resetPaymentForm(); setShowPaymentForm(true); }}
              className="flex items-center gap-1"
            >
              <Plus className="w-4 h-4" /> Add New
            </Button>
          )}
        </div>

        {showPaymentForm ? (
          <form onSubmit={handlePaymentSubmit} className="bg-muted/30 p-4 rounded-lg border border-border space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Input 
                  label="Card Nickname (e.g. My Personal Card)" 
                  value={paymentFormData.nickname}
                  onChange={(e) => setPaymentFormData({...paymentFormData, nickname: e.target.value})}
                />
              </div>
              <div className="md:col-span-2">
                <Input 
                  label="Cardholder Name" 
                  required 
                  value={paymentFormData.cardHolderName}
                  onChange={(e) => setPaymentFormData({...paymentFormData, cardHolderName: e.target.value})}
                  disabled={!!editingPaymentMethod}
                />
              </div>
              <div className="md:col-span-2 flex flex-col md:flex-row gap-4 items-end">
                <div className="w-full md:w-64 flex-shrink-0">
                    <div className="flex justify-between items-center mb-1">
                      <label className="text-xs font-medium text-muted-foreground">Card Number <span className="text-error">*</span></label>
                      <span className="text-xs font-semibold text-primary animate-in fade-in">
                          {editingPaymentMethod ? (paymentFormData.cardBrand || 'Card') : detectedBrand}
                      </span>
                    </div>
                    {editingPaymentMethod ? (
                        <Input 
                          value={paymentFormData.cardNumber}
                          disabled
                        />
                    ) : (
                        <div id="card-number-element" className="block h-10 w-full rounded-md border border-input bg-muted px-3 py-2.5" />
                    )}
                </div>
                
                <div className="w-full md:w-28 flex-shrink-0">
                    {editingPaymentMethod ? (
                        <Input 
                            label="Expiry" 
                            value={paymentFormData.expiryDate}
                            disabled
                        />
                    ) : (
                        <div className="space-y-1">
                            <label className="text-xs font-medium text-muted-foreground">Expiry <span className="text-error">*</span></label>
                            <div id="card-expiry-element" className="block h-10 w-full rounded-md border border-input bg-muted px-3 py-2.5" />
                        </div>
                    )}
                </div>

                <div className="w-full md:w-20 flex-shrink-0">
                   {editingPaymentMethod ? (
                       <Input 
                        label="CVV" 
                        value={paymentFormData.cvv}
                        disabled
                      />
                   ) : (
                       <div className="space-y-1">
                            <label className="text-xs font-medium text-muted-foreground">CVV <span className="text-error">*</span></label>
                            <div id="card-cvc-element" className="block h-10 w-full rounded-md border border-input bg-muted px-3 py-2.5" />
                       </div>
                   )}
                </div>
              </div>

              <div className="md:col-span-2 flex items-center h-10">
                 <label className="flex items-center gap-2 cursor-pointer text-sm whitespace-nowrap">
                    <input 
                        type="checkbox" 
                        className="w-4 h-4 rounded border-gray-500 text-primary focus:ring-primary/20" 
                        checked={paymentFormData.isDefault}
                        onChange={(e) => setPaymentFormData({...paymentFormData, isDefault: e.target.checked})}
                    />
                    Set as default
                 </label>
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="ghost" type="button" onClick={() => { setShowPaymentForm(false); setEditingPaymentMethod(null); }}>Cancel</Button>
              <Button variant="primary" type="submit">{editingPaymentMethod ? 'Update' : 'Save'} Payment Method</Button>
            </div>
          </form>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {paymentMethods.length > 0 ? (
              paymentMethods.map((method) => (
                <div key={method.id} className="p-4 border border-border rounded-lg bg-card hover:bg-muted/20 transition-smooth relative group flex items-center gap-4">
                  <div className="w-10 h-10 bg-muted rounded flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-foreground capitalize">
                        {method.nickname || `${method.cardBrand || 'Card'} ending in ${method.cardLastFour}`}
                      </p>
                      {method.isDefault && (
                        <span className="text-[10px] font-medium text-accent bg-accent/10 px-2 py-0.5 rounded flex items-center gap-1">
                          <Check className="w-3 h-3" /> Default
                        </span>
                      )}
                    </div>
                    {method.nickname && (
                      <p className="text-xs text-muted-foreground">{method.cardBrand || 'Card'} ending in {method.cardLastFour}</p>
                    )}
                    <p className="text-sm text-muted-foreground">{method.cardHolderName}</p>
                    <p className="text-xs text-muted-foreground">Expires {method.cardExpMonth}/{method.cardExpYear}</p>
                  </div>
                  
                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {!method.isDefault && (
                        <button onClick={() => handleSetDefaultPayment(method.id)} className="p-1.5 hover:bg-muted rounded text-muted-foreground hover:text-primary" title="Set as Default">
                           <Check className="w-3.5 h-3.5" />
                        </button>
                    )}
                    <button onClick={() => handleEditPayment(method)} className="p-1.5 hover:bg-muted rounded text-muted-foreground hover:text-foreground" title="Edit Nickname">
                        <Icon name="Edit" size={14} />
                    </button>
                    <button onClick={() => handleDeletePayment(method)} className="p-1.5 hover:bg-error/10 rounded text-muted-foreground hover:text-error" title="Delete">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-2 text-center py-6 text-muted-foreground bg-muted/20 rounded-lg border border-dashed border-border">
                No payment methods saved yet.
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
